import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../../kit";
import { useUi } from "../../../state/editor/useEditor";
import { chromeInsets } from "../layout";
import { VIEWER_ORIGIN } from "../library/viewerOrigin";
import { photoToScreen, screenToPhoto } from "./framing";
import { manualPrompt, useOnBody, type OnBodySession } from "./session";
import type { Vec2 } from "./anchorMath";

/** The viewer iframe — the one element whose box IS the canvas. */
function viewerIframe(): HTMLIFrameElement | null {
  return document.querySelector<HTMLIFrameElement>(`iframe[src^="${VIEWER_ORIGIN}"]`);
}

/** The iframe's box, kept current through resizes. Measured only while `on`. */
function useCanvasRect(on: boolean): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    if (!on) {
      setRect(null);
      return;
    }
    const el = viewerIframe();
    if (!el) return;
    const read = () => setRect(el.getBoundingClientRect());
    read();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(read) : null;
    ro?.observe(el);
    window.addEventListener("resize", read);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", read);
    };
  }, [on]);
  return rect;
}

/**
 * Manual placement: click the photo where the body part is.
 *
 * The canvas is a cross-origin iframe, so the host cannot see clicks that land
 * on it. While — and only while — the session is collecting clicks, a
 * transparent layer covers the iframe's box and takes them, converting each
 * through the LIVE backdrop framing (the viewer's own matrix, `framing.ts`) into
 * photo pixels. A finger takes two clicks (knuckle, middle joint) — its width
 * is then measured from the photo — a neck three (base, chin, one side).
 * It sits at z-index 98, under the editor chrome's 99, so the rails and
 * panels stay clickable over it; it is unmounted the moment the last
 * click lands, Escape is pressed, or the panel closes — it never sits over the
 * canvas blocking orbit.
 */
export function ManualOverlay({ session }: { session: OnBodySession }) {
  const s = useOnBody(session);
  const m = s.manual;
  const photo = s.photo;
  const rect = useCanvasRect(!!m && !!photo);
  const [ui] = useUi();
  const [hover, setHover] = useState<Vec2 | null>(null);

  useEffect(() => {
    if (!m) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") session.cancelManual();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [m, session]);

  if (!m || !photo || !rect || rect.width < 2 || rect.height < 2) return null;

  const viewAspect = rect.width / rect.height;
  const imageAspect = photo.w / photo.h;
  // URL backdrops are flipped on upload; a picked file is an ImageBitmap,
  // which is not — the viewer's `_backdropMapping` decides the same way.
  const flipY = !photo.fromFile;
  const toPhotoPx = (cx: number, cy: number): Vec2 => {
    const [x, y] = screenToPhoto(m.framing, viewAspect, imageAspect, flipY, (cx - rect.left) / rect.width, (cy - rect.top) / rect.height);
    return [x * photo.w, y * photo.h];
  };
  const toLocal = (p: Vec2): Vec2 | null => {
    const sc = photoToScreen(m.framing, viewAspect, imageAspect, flipY, p[0] / photo.w, p[1] / photo.h);
    return sc ? [sc[0] * rect.width, sc[1] * rect.height] : null;
  };

  const pts = m.points.map(toLocal).filter((p): p is Vec2 => !!p);
  const step = m.points.length;
  // The width line (a neck's third click): across the p0 → p1 axis, through
  // the point under the cursor. A finger's width is measured, not clicked.
  let widthLine: [Vec2, Vec2] | null = null;
  if (m.needed > 2 && pts.length >= 2 && hover) {
    const [a, b] = pts;
    const d: Vec2 = [b[0] - a[0], b[1] - a[1]];
    const len = Math.hypot(d[0], d[1]) || 1;
    const n: Vec2 = [-d[1] / len, d[0] / len];
    const h: Vec2 = [hover[0] - rect.left, hover[1] - rect.top];
    const off = (h[0] - a[0]) * n[0] + (h[1] - a[1]) * n[1];
    const mid: Vec2 = m.kind === "finger" ? [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] : a;
    widthLine = [
      [mid[0] - n[0] * off, mid[1] - n[1] * off],
      [mid[0] + n[0] * off, mid[1] + n[1] * off],
    ];
  }

  // The prompt sits over the part of the canvas the panels leave visible.
  const insets = chromeInsets(ui.leftPanel, ui.inspectorMode === "basic" ? null : ui.rightTab, window.innerWidth, ui.inspectorMode);
  const gapLeft = Math.max(rect.left, insets.left);
  const gapRight = Math.min(rect.right, window.innerWidth - insets.right);
  const promptX = (gapLeft + gapRight) / 2;

  const accent = "#946f2e";
  return createPortal(
    <>
      <div
        data-onbody-overlay
        role="presentation"
        className="fixed"
        style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height, zIndex: 98, cursor: "crosshair" }}
        onPointerMove={(e) => setHover([e.clientX, e.clientY])}
        onPointerLeave={() => setHover(null)}
        onClick={(e) => void session.manualPoint(toPhotoPx(e.clientX, e.clientY))}
      >
        <svg width={rect.width} height={rect.height} className="pointer-events-none absolute inset-0">
          {pts.length >= 2 ? (
            <line x1={pts[0][0]} y1={pts[0][1]} x2={pts[1][0]} y2={pts[1][1]} stroke="#fff" strokeWidth={4} strokeLinecap="round" />
          ) : null}
          {pts.length >= 2 ? (
            <line x1={pts[0][0]} y1={pts[0][1]} x2={pts[1][0]} y2={pts[1][1]} stroke={accent} strokeWidth={2} strokeLinecap="round" />
          ) : null}
          {widthLine ? (
            <line
              x1={widthLine[0][0]}
              y1={widthLine[0][1]}
              x2={widthLine[1][0]}
              y2={widthLine[1][1]}
              stroke={accent}
              strokeWidth={2}
              strokeDasharray="5 4"
            />
          ) : null}
          {pts.map((p, i) => (
            <g key={i}>
              <circle cx={p[0]} cy={p[1]} r={7} fill="#fff" />
              <circle cx={p[0]} cy={p[1]} r={5} fill={accent} />
            </g>
          ))}
        </svg>
      </div>
      <div
        data-onbody-prompt
        className="fixed flex -translate-x-1/2 items-center gap-2 rounded-xl border border-ed-border bg-ed-panel py-1.5 pl-3 pr-1.5 text-[13px] text-ed-text shadow-[0_6px_20px_rgba(0,0,0,0.12)]"
        style={{ left: promptX, top: rect.top + 64, zIndex: 120 }}
      >
        <span className="tabular-nums text-ed-text-3">
          {Math.min(step + 1, m.needed)} of {m.needed}
        </span>
        <span className="font-medium">{manualPrompt(m.kind, step, s.finger)}</span>
        <button
          type="button"
          onClick={() => session.cancelManual()}
          className="ml-1 inline-flex h-7 items-center gap-1 rounded-lg px-2 text-[12px] text-ed-text-2 hover:bg-ed-hover hover:text-ed-text"
          title="Stop placing (Esc)"
        >
          <Icon name="close" size={15} />
          Cancel
        </button>
      </div>
    </>,
    document.body,
  );
}
